import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { openCalendly } from "@/integrations/calendly";

const links = [
  { label: "Home", href: "#top" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Solutions", href: "#solutions" },
  { label: "Scorecard", href: "/scorecard" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [contactVisible, setContactVisible] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      setPastHero(window.scrollY > window.innerHeight * 0.8);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const els = ["#contact", "#how-it-works", "#audit", "#growth-system", "#use-cases", "#calculator", "#scorecard", "#process", "#proof"].map((s) => document.querySelector(s)).filter(Boolean);
    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) visible.add(e.target.id);
          else visible.delete(e.target.id);
        });
        setContactVisible(visible.size);
      },
      { threshold: 0.05 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 ${
          scrolled && !open ? "backdrop-blur-xl bg-[#0B0F19]/85 border-b border-white/10" : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <a href="#top" data-testid="navbar-logo" onClick={close} className="flex items-center gap-2.5">
            <span className="bg-white/95 rounded-xl px-2 py-1 flex items-center">
              <img src="/ivibes-cloud.png" alt="iVIBES logo" className="h-7 w-auto" />
            </span>
            <span className="font-heading text-xl tracking-tight font-extrabold">
              <span className="text-cyan-400">i</span>VIBES
              <span className="text-xl font-medium text-slate-200 ml-1.5 tracking-normal">Consulting</span>
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-slate-200 hover:text-cyan-300 transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => openCalendly()}
              data-testid="nav-book-call-button"
              data-track="homepage_lead_flow_review_clicked"
              className="rounded-full px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-sm font-semibold text-white transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(99,102,241,0.45)]"
            >
              Book Your Lead Flow Review
            </button>
          </div>

          <button
            data-testid="nav-mobile-menu-button"
            className="lg:hidden text-slate-100 p-3 -mr-3"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
      </header>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="fixed inset-0 z-40 lg:hidden bg-[#0B0F19] flex flex-col px-8 pt-28 pb-10"
        >
          <nav className="flex-1 flex flex-col">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={close}
                className="font-heading font-bold text-2xl py-5 border-b border-white/10 text-white active:text-cyan-300 transition-colors duration-150"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <button
            type="button"
            data-testid="nav-mobile-book-call-button"
            data-track="homepage_lead_flow_review_clicked"
            onClick={() => {
              close();
              openCalendly();
            }}
            className="rounded-full px-6 py-4 bg-indigo-600 text-center text-lg font-semibold text-white"
          >
            Book Your Lead Flow Review
          </button>
        </div>
      )}

      {!open && pastHero && contactVisible === 0 && (
        <div data-testid="mobile-sticky-cta" className="fixed bottom-4 inset-x-4 z-40 lg:hidden">
          <button
            type="button"
            onClick={() => openCalendly()}
            data-testid="mobile-sticky-cta-button"
            data-track="homepage_lead_flow_review_clicked"
            className="block w-full rounded-full px-6 py-4 bg-indigo-600 text-center font-semibold text-white shadow-[0_8px_30px_rgba(0,0,0,0.5)]"
          >
            Book Your Lead Flow Review
          </button>
        </div>
      )}
    </>
  );
}
